/**
 * Making sure a solid prop is somewhere you can still walk past.
 *
 * Props used to be scenery the player walked through, so the only rule was
 * "put it where nobody would try to walk". A bathtub standing in the middle of
 * a room is a real obstacle, and an obstacle in the wrong place is the one
 * failure this whole feature can produce that a player will actually feel: a
 * corridor plugged by a filing cabinet is a level they cannot finish.
 *
 * The test is deliberately coarser than the runtime collision. A prop claims
 * every grid cell its footprint touches once grown by the player's radius, and
 * a claimed cell counts as impassable even if only a corner of it is really
 * covered. That over-states the blockage, which is the safe direction: anything
 * this pass accepts, the player can definitely get through. What it costs is
 * the occasional good spot turned down in a tight room, which nobody notices.
 *
 * Reachability is checked in a window around the prop rather than over the
 * whole level. Sound, because every cell orthogonally adjacent to the prop is
 * inside the window: if the prop severs a route, the two sides of the cut are
 * both in there, and the test insists they reconnect *within* the window. A
 * pair that could only reconnect by a long way round outside it is rejected —
 * conservative again, and it also keeps the pass linear in the number of props
 * rather than in props times level area.
 */

import { TUNING } from '../../core/config';
import { Cell, type PropFootprint } from '../../core/types';
import type { PropSites } from './propSites';

/**
 * Metres of slack added to the player's radius before a footprint claims a
 * cell. Enough that "passable" means comfortably passable rather than a scrape
 * along a wall with the camera inside the geometry.
 */
const CLEARANCE = TUNING.player.radius + 0.08;

/** Cells searched either side of the prop when checking a route survives. */
const WINDOW_PAD = 3;

/**
 * Cells whose square overlaps the footprint, in the prop's own frame, grown by
 * `CLEARANCE`. Fills and returns `out`, which is cleared first.
 *
 * Separating-axis test rather than a bounding box: most of these props are long
 * and thin — a half-wall is three metres by thirty centimetres — and the AABB
 * of one at forty degrees is nearly three times its real area, which would turn
 * down most of the diagonal placements that make a room read as wrong.
 */
export function footprintCells(
  sites: PropSites,
  px: number,
  pz: number,
  yaw: number,
  fp: PropFootprint,
  scale: number,
  out: number[],
): number[] {
  out.length = 0;
  const grid = sites.grid;
  const size = grid.cellSize;
  const half = size * 0.5;

  const ex = fp.halfX * scale + CLEARANCE;
  const ez = fp.halfZ * scale + CLEARANCE;
  // yaw = 0 faces -Z, so the prop's local +X is (cos, sin) and local +Z is
  // (-sin, cos). Same convention as `core/props.ts`, which does the real test.
  const ux = Math.cos(yaw);
  const uz = Math.sin(yaw);
  const vx = -uz;
  const vz = ux;

  // World-space reach of the box, for the cells worth testing at all.
  const reachX = ex * Math.abs(ux) + ez * Math.abs(vx);
  const reachZ = ex * Math.abs(uz) + ez * Math.abs(vz);
  const x0 = Math.floor((px - reachX) / size);
  const x1 = Math.floor((px + reachX) / size);
  const z0 = Math.floor((pz - reachZ) / size);
  const z1 = Math.floor((pz + reachZ) / size);

  // The square's radius projected onto the box's own axes is the same for both,
  // since the square is symmetric: half * (|axis.x| + |axis.z|).
  const squareOnU = half * (Math.abs(ux) + Math.abs(uz));
  const squareOnV = half * (Math.abs(vx) + Math.abs(vz));

  for (let cy = z0; cy <= z1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      if (!grid.inBounds(cx, cy)) continue;
      const c = grid.cellToWorld(cx, cy);
      const dx = c.x - px;
      const dz = c.z - pz;
      if (Math.abs(dx) > half + reachX) continue;
      if (Math.abs(dz) > half + reachZ) continue;
      if (Math.abs(dx * ux + dz * uz) > ex + squareOnU) continue;
      if (Math.abs(dx * vx + dz * vz) > ez + squareOnV) continue;
      out.push(cy * grid.w + cx);
    }
  }
  return out;
}

/**
 * True when nothing that must stay clear is under the footprint. Solid cells
 * are ignored on purpose: a wall is already impassable, and a prop backed onto
 * one is meant to overlap it.
 */
export function cellsAcceptable(sites: PropSites, cells: readonly number[]): boolean {
  const cellsArr = sites.grid.cells;
  for (const i of cells) {
    const c = cellsArr[i] as Cell;
    if (c === Cell.Void || c === Cell.Wall) continue;
    // A doorway is the one place a prop must never be, and a pillar cell
    // already has a column through the middle of it.
    if (c === Cell.Doorway || c === Cell.Pillar) return false;
    if (sites.reserved[i] || sites.blocked[i]) return false;
  }
  return true;
}

/**
 * True when blocking `cells` leaves every route through the neighbourhood
 * intact. Compares the local connectivity before and after: each group of cells
 * that could reach each other with the prop absent must still be able to with
 * it there.
 */
export function passageSurvives(sites: PropSites, cells: readonly number[]): boolean {
  const grid = sites.grid;
  const { w, h } = grid;

  let minX = w;
  let maxX = -1;
  let minY = h;
  let maxY = -1;
  for (const i of cells) {
    const x = i % w;
    const y = (i / w) | 0;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (maxX < 0) return true;

  const x0 = Math.max(0, minX - WINDOW_PAD);
  const x1 = Math.min(w - 1, maxX + WINDOW_PAD);
  const y0 = Math.max(0, minY - WINDOW_PAD);
  const y1 = Math.min(h - 1, maxY + WINDOW_PAD);
  const ww = x1 - x0 + 1;
  const wh = y1 - y0 + 1;

  // 0 = solid or already blocked, 1 = free, 2 = free but claimed by this prop.
  const state = new Uint8Array(ww * wh);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = y * w + x;
      if (!grid.isWalkable(x, y) || sites.blocked[i]) continue;
      state[(y - y0) * ww + (x - x0)] = 1;
    }
  }
  for (const i of cells) {
    const x = i % w;
    const y = (i / w) | 0;
    if (x < x0 || x > x1 || y < y0 || y > y1) continue;
    const k = (y - y0) * ww + (x - x0);
    if (state[k] === 1) state[k] = 2;
  }

  const before = new Int16Array(ww * wh).fill(-1);
  const after = new Int16Array(ww * wh).fill(-1);
  const queue = new Int32Array(ww * wh);
  label(state, before, queue, ww, wh, 2);
  label(state, after, queue, ww, wh, 1);

  // Every component of the un-propped neighbourhood must survive as exactly one
  // component once the prop is in. Cells the prop itself claims simply vanish.
  const seen = new Int16Array(ww * wh).fill(-1);
  for (let k = 0; k < before.length; k++) {
    const b = before[k]!;
    const a = after[k]!;
    if (b < 0 || a < 0) continue;
    const known = seen[b]!;
    if (known < 0) seen[b] = a;
    else if (known !== a) return false;
  }
  return true;
}

/**
 * Flood-fill component labels over the window. `maxState` is 2 to treat the
 * prop's own cells as passable (the "before" pass) and 1 to treat them as
 * solid (the "after" pass).
 */
function label(
  state: Uint8Array,
  out: Int16Array,
  queue: Int32Array,
  ww: number,
  wh: number,
  maxState: number,
): void {
  let next = 0;
  for (let seed = 0; seed < state.length; seed++) {
    if (out[seed] !== -1) continue;
    const s = state[seed]!;
    if (s === 0 || s > maxState) continue;
    let head = 0;
    let tail = 0;
    out[seed] = next;
    queue[tail++] = seed;
    while (head < tail) {
      const k = queue[head++]!;
      const x = k % ww;
      const y = (k / ww) | 0;
      for (let d = 0; d < 4; d++) {
        if (d === 0 && x === 0) continue;
        if (d === 1 && x === ww - 1) continue;
        if (d === 2 && y === 0) continue;
        if (d === 3 && y === wh - 1) continue;
        const j = d === 0 ? k - 1 : d === 1 ? k + 1 : d === 2 ? k - ww : k + ww;
        if (out[j] !== -1) continue;
        const v = state[j]!;
        if (v === 0 || v > maxState) continue;
        out[j] = next;
        queue[tail++] = j;
      }
    }
    next++;
  }
}
